import { Stack } from "@mui/material";
import { FC, useMemo } from "react";
import { useToggle } from "rooks";
import { hierarchyHasChildren } from "@/components/pages/infrastructure-architecture/utils/helpers/hierarchyHasChildren";
import { InfrastructureArchitectureHierarchyItemContent } from "@/components/pages/infrastructure-architecture/components/select-panel/body/hierarchy/InfrastructureArchitectureHierarchyItemContent";
import { infrastructureArchitectureSelectedResourceIdSelector } from "@/store/infrastructure-architecture/selectors/infrastructureArchitectureSelectedResourceIdSelector";
import { useAppSelector } from "@/store/hooks";
import { InfrastructureArchitectureHierarchySwitch } from "@/components/pages/infrastructure-architecture/components/select-panel/body/hierarchy/InfrastructureArchitectureHierarchySwitch";

interface InfrastructureArchitectureHierarchyItemProps {
  hierarchyLevel: number;
  resourceName?: string;
  resourceLabel?: string;
  hierarchy?: any;
}

export const InfrastructureArchitectureHierarchyItem: FC<
  InfrastructureArchitectureHierarchyItemProps
> = ({ hierarchyLevel, resourceName, resourceLabel, hierarchy }) => {
  const selectedResourceId = useAppSelector(
    infrastructureArchitectureSelectedResourceIdSelector,
  );
  const childIsExpanded = useMemo(() => {
    return isExpanded(hierarchy, selectedResourceId);
  }, [hierarchy, selectedResourceId]);

  const [expanded, toggleExpanded] = useToggle(
    hierarchyLevel === 0 || childIsExpanded,
  );

  const resourceId = hierarchy?.id;
  const selected =
    (!!selectedResourceId && resourceId === selectedResourceId) ||
    (!selectedResourceId && hierarchyLevel === 0);
  const hasChildren = hierarchyHasChildren(hierarchy);

  return (
    <Stack
      ml={hierarchyLevel === 0 ? 0 : 2.5}
      sx={{
        border: 1,
        borderRadius: 1.25,
        borderColor: selected ? "primary.main" : "white",
        cursor: resourceId && hasChildren ? "pointer" : "default",
      }}
    >
      <InfrastructureArchitectureHierarchyItemContent
        selected={selected}
        expanded={expanded}
        resourceId={resourceId}
        hasChildren={hasChildren}
        resourceName={resourceName}
        resourceType={resourceLabel}
        toggleExpanded={toggleExpanded}
      />

      {hierarchy && expanded && (
        <InfrastructureArchitectureHierarchySwitch
          hierarchy={hierarchy}
          hierarchyLevel={hierarchyLevel}
        />
      )}
    </Stack>
  );
};

const isExpanded = (hierarchy: any, resourceId?: string): boolean => {
  if (!hierarchy || !resourceId) {
    return false;
  }

  if (typeof hierarchy === "string") {
    return resourceId === hierarchy;
  }

  if (typeof hierarchy !== "object") {
    return false;
  }

  if (hierarchy?.id === resourceId) {
    return true;
  }

  return Object.values(hierarchy)
    .map((child: any) => isExpanded(child, resourceId))
    .some((childExpanded: boolean) => childExpanded);
};
